import jwtDecode from "jwt-decode";
import { showAlert } from "../../components/general-ui/alert/alert-service";
import { fetchLiveEventBundle } from "../../connection/live-events/event";
import { EValidateRegistrationResponseType, ReRegisterCheck, ValidateRegistration } from "../../connection/registration";
import { BlProfile, BrandliveEvent, Dictionary } from "../../types/working-model";
import { getStorageItem, removeStorageItem, setStorageItem } from "../../utils/local-storage";
import { matchPath } from "react-router";

interface PageLoadResponse {
	registrationId: string | null;
	eventBundleError: string;
	playbackUrls: Dictionary;
	userVerified: boolean;
	mustVerify: boolean;
	registrationStep: number;
	validationFailure: boolean;
	blProfileUser: BlProfile | null;
	blProfileUserToken: string | null;
	shouldRedirectToRegistration: boolean;
	_404: boolean;
	eventBundle?: BrandliveEvent;
	validRegistration: boolean;
	validSessions: string[];
	paymentRequired: boolean;
	validationChecked: boolean;
	paidSessions?: string[];
}

export async function LoadEventBundle(channelHost: string, eventUrl: string): Promise<{ eventBundle: BrandliveEvent | null, _404: boolean }> {
	try {
		const eventBundle = await fetchLiveEventBundle(channelHost, eventUrl);

		const initialState = {
			eventBundle: null,
			_404: false
		};

		if (('message' in eventBundle && eventBundle.message === 'Internal Server Error') || 'error' in eventBundle) {
			console.error(eventBundle);
			initialState._404 = true;
			return initialState;
		}

		initialState.eventBundle = eventBundle;
		return initialState;
	} catch (e) {
		console.error(e);
		return { _404: true, eventBundle: null };
	}

}

export async function LoadPage(eventBundle: BrandliveEvent): Promise<PageLoadResponse> {
	const initialState: PageLoadResponse = {
		registrationId: null,
		eventBundleError: '',
		playbackUrls: {},
		userVerified: false,
		mustVerify: false,
		registrationStep: 0,
		validationFailure: false,
		blProfileUserToken: null,
		blProfileUser: null,
		shouldRedirectToRegistration: false,
		_404: false,
		validRegistration: true,
		validSessions: [],
		paymentRequired: false,
		validationChecked: false,
	};

	try {
		const registrationId = getStorageItem(`reg.${eventBundle.uuid}`);
		const blToken = getStorageItem('bl-profile');
		const blProfileUser: BlProfile | null = blToken ? jwtDecode(blToken) as BlProfile : null;
		const queryParams = new URLSearchParams(window.location.search);
		const localStorageRegStepKey = `registration-step-${eventBundle.uuid}`;
		const savedStep = Number(getStorageItem(localStorageRegStepKey));

		if (savedStep) {
			initialState.registrationStep = savedStep;
		}

		initialState.blProfileUser = blProfileUser as BlProfile;
		initialState.blProfileUserToken = blToken;

		initialState.registrationId = registrationId;

		//we have a stored registration id for this user for this session
		if (registrationId && eventBundle.registration_on) {
			try {
				//validates that the registration id is valid and hasn't been messed with - if token is not null, send it, otherwise leave it undefined
				const valid = await ValidateRegistration(eventBundle.uuid, registrationId, blToken ?? undefined);
				if (valid?.paidSessions) {
					initialState.paidSessions = valid.paidSessions;
				}

				//validation error - remove registration id
				if ("message" in valid || "error" in valid) {
					console.error(valid);
					initialState.validationFailure = true;
					initialState.validRegistration = false;
					initialState.validationChecked = true;
					removeStorageItem(`reg.${eventBundle.uuid}`);
					return initialState;
				}

				//validation was not valid, remove registration id
				if (valid.response === EValidateRegistrationResponseType.invalid) {
					initialState.validationFailure = true;
					initialState.validRegistration = false;
					initialState.validationChecked = true;
					removeStorageItem(`reg.${eventBundle.uuid}`);
					return initialState;
				}

				const confirmationRequired = eventBundle.registration_settings?.registrationConfirmation;

				//user has registered, but has not verified their profile, force them back to verify profile
				initialState.mustVerify = !!confirmationRequired && !getStorageItem('user-code');
				initialState.playbackUrls = valid.playbackUrls;
				initialState.validSessions = valid.validSessions;
				initialState.paymentRequired = valid.paymentRequired;
				initialState.validationChecked = true;

				//update event bundle with sessions from validate registration
				initialState.eventBundle = {
					...eventBundle,
					sessions: valid.bundle!.sessions,
				};

				//set registration id
				initialState.registrationId = registrationId;
				initialState.validRegistration = true;

				//user refreshed before completing form, but has reg id from general content view - make them complete
				if (initialState.mustVerify) {
					initialState.shouldRedirectToRegistration = true;
				}
			} catch (e) {
				console.error(e);
				initialState.validationFailure = true;
				initialState.validationChecked = true;
				return initialState;
			}
		} else if (eventBundle.playbackUrls) {
			initialState.playbackUrls = eventBundle.playbackUrls;
		}

		const reRegisterCode = queryParams.get('reg-code');

		if (reRegisterCode) {
			try {
				//checks the re-register query param and returns the first registration used by user and their token
				//works essentially the same as the registration call
				const response = await ReRegisterCheck(eventBundle.uuid, reRegisterCode);

				//was an invalid token, reset them to force re-registration
				if ('message' in response || 'error' in response || "used" in response) {
					initialState.userVerified = false;
					initialState.shouldRedirectToRegistration = true;
					initialState.mustVerify = true;

					//need to force this user to re-register
					removeStorageItem(`reg.${eventBundle.uuid}`);

					//confirmation code has essentially been used up. Need to direct them back to registration
					if ("used" in response) {

						//remove should continue reg
						removeStorageItem(`should-cont-reg-${eventBundle.uuid}`);

						//clear last registration step so they can go back to 0
						removeStorageItem(localStorageRegStepKey);

						//we don't want to force them to the verification step, we want them back at the first step
						initialState.mustVerify = false;
						initialState.registrationStep = 0;

						//display alert telling user that the code is used up
						showAlert({
							message: "Registration Link Used",
							description: "Sorry, it looks like that email link has already been used. Please register again or click the 'Already Registered' link.",
							type: "error",
							duration: 10000
						});
					}

					return initialState;
				}

				//reg code succeeded
				const {
					token,
					validSessions,
					secureEvent,
					playbackUrls,
					userCode,
					registrationId,
					paymentRequired
				} = response;

				//user is essentially verified entirely by this email
				//and the full event is now loaded
				setStorageItem('bl-profile', token);
				setStorageItem('Bl-Profile-Token', token);
				setStorageItem('user-code', userCode!);
				removeStorageItem(`should-cont-reg-${eventBundle.uuid}`);
				setStorageItem(`reg.${eventBundle.uuid}`, registrationId);

				initialState.playbackUrls = playbackUrls;
				initialState.validSessions = validSessions;
				initialState.paymentRequired = paymentRequired;
				initialState.userVerified = true;
				initialState.shouldRedirectToRegistration = false;
				initialState.mustVerify = false;

				//update event bundle with sessions from validate registration
				initialState.eventBundle = {
					...eventBundle,
					sessions: secureEvent!.sessions,
				};

				//set registration id
				initialState.registrationId = registrationId;
				initialState.validRegistration = true;

				//drop the code from the url in case they refresh, they shouldn't have to be re-checked.
				window.history.replaceState(undefined, document.title, window.location.href.split("?")[0]);
			} catch (e) {
				initialState.userVerified = false;
				initialState.shouldRedirectToRegistration = true;
			}
		}

		//registration is on and user has never registered
		if (eventBundle.registration_on && !registrationId) {

			//should check a set of pages that should only be accessible post registration
			const match = matchPath(window.location.pathname, {
				path: [
					"/event/:uuid/:language/session/:sessionUuid",
					"/:eventName/:language/session/:sessionUuid",
					"/event/:uuid/:language/home",
					"/:eventName/:language/home",
					"/event/:uuid/:language/sessions",
					"/:eventName/:language/sessions"
				],
				exact: true
			});

			//only redirect if the current path matches a session page
			initialState.shouldRedirectToRegistration = !!match;
		}

		return initialState;
	} catch (e) {
		console.error(e);
		return initialState;
	}
}